import { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckSquare, X, Edit } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

/**
 * Barra y Modal para edición masiva de assets
 * 
 * @param {Object} props
 * @param {Array} props.selectedIds - IDs seleccionados
 * @param {Function} props.onClear - Función para limpiar selección
 * @param {Function} props.onSuccess - Callback después de actualización exitosa
 * @param {String} props.endpoint - Endpoint base (ej: '/assets/workstations')
 * @param {Object} props.options - Opciones para los dropdowns { sites, empresas, statuses, ubicaciones }
 * @param {Array} props.fields - Campos disponibles para editar ['site_id', 'empresa_id', 'status_id', 'location_id']
 * @param {Object} props.fieldMapping - Mapeo de campos si tienen nombres diferentes { site: 'site_id' }
 */
const BulkEditBar = ({ 
  selectedIds = [], 
  onClear, 
  onSuccess,
  endpoint,
  options = {},
  fields = ['site_id', 'empresa_id', 'status_id', 'location_id'],
  fieldMapping = {}
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bulkData, setBulkData] = useState({});

  // Si no hay selección, no mostrar nada
  if (selectedIds.length === 0) return null;

  // Mapear nombres de campos internos a los del formulario
  const getFieldName = (field) => fieldMapping[field] || field;

  // Configuración de campos
  const fieldConfig = {
    site_id: { label: 'Site', optionsKey: 'sites', nameKey: 'nombre' },
    site: { label: 'Site', optionsKey: 'sites', nameKey: 'nombre' },
    sitio: { label: 'Site', optionsKey: 'sites', nameKey: 'nombre' },
    empresa_id: { label: 'Empresa', optionsKey: 'empresas', nameKey: 'nombre' },
    status_id: { label: 'Estado', optionsKey: 'statuses', nameKey: 'nombre' },
    estado: { label: 'Estado', optionsKey: 'estados', nameKey: 'nombre' },
    location_id: { label: 'Ubicación', optionsKey: 'ubicaciones', nameKey: 'nombre' },
    ubicacion: { label: 'Ubicación', optionsKey: 'ubicaciones', nameKey: 'nombre' },
    Location: { label: 'Ubicación', optionsKey: 'ubicaciones', nameKey: 'nombre' },
    brand_id: { label: 'Marca', optionsKey: 'brands', nameKey: 'nombre' },
    dvr_id: { label: 'DVR/NVR', optionsKey: 'dvrs', nameKey: 'nombre' },
    id_tipo_dispositivo: { label: 'Tipo', optionsKey: 'tipos', nameKey: 'nombre' },
    os: { label: 'Sistema Operativo', optionsKey: 'os', nameKey: 'nombre' },
    assigned_user_id: { label: 'Usuario Asignado', optionsKey: 'usuarios', nameKey: 'nombre' },
  };

  const handleOpenModal = () => {
    setBulkData({});
    setShowModal(true);
  };

  const handleSubmit = async () => {
    // Verificar que al menos un campo esté seleccionado
    const hasChanges = Object.values(bulkData).some(v => v && v !== 'no_change');
    if (!hasChanges) {
      toast.error('Selecciona al menos un campo para actualizar');
      return;
    }

    setLoading(true);
    try {
      // Preparar payload - solo enviar campos que cambiaron
      const payload = { ids: selectedIds };
      Object.entries(bulkData).forEach(([key, value]) => {
        if (value && value !== 'no_change') {
          payload[key] = parseInt(value);
        }
      });

      await api.put(`${endpoint}/bulk-update`, payload);
      toast.success(`${selectedIds.length} registros actualizados exitosamente`);
      setShowModal(false);
      onClear();
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Barra de selección */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
        <div className="flex items-center gap-3 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg">
          <CheckSquare className="h-5 w-5" />
          <span className="font-medium">{selectedIds.length} seleccionados</span>
          <div className="h-4 w-px bg-primary-foreground/30" />
          <Button 
            size="sm" 
            variant="secondary"
            onClick={handleOpenModal}
            className="h-7"
          >
            <Edit className="h-3 w-3 mr-1" />
            Editar masivo
          </Button>
          <Button 
            size="sm" 
            variant="ghost"
            onClick={onClear}
            className="h-7 text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Modal de edición masiva */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" aria-describedby="bulk-edit-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edición Masiva ({selectedIds.length} registros)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p id="bulk-edit-description" className="text-sm text-muted-foreground">
              Solo se actualizarán los campos que selecciones. Los demás permanecerán sin cambios.
            </p>

            {fields.map(field => {
              const config = fieldConfig[field];
              if (!config) return null;
              
              const fieldOptions = options[config.optionsKey] || [];
              
              return (
                <div key={field} className="space-y-2">
                  <Label>{config.label}</Label>
                  <Select
                    value={bulkData[field] || 'no_change'}
                    onValueChange={(v) => setBulkData(prev => ({ ...prev, [field]: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin cambios" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_change">— Sin cambios —</SelectItem>
                      {fieldOptions.map(opt => (
                        <SelectItem key={opt.id} value={String(opt.id)}>
                          {opt[config.nameKey] || opt.name || opt.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? 'Actualizando...' : 'Aplicar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkEditBar;
